import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowRight, Loader2, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { trackPurchase } from '../lib/analytics';
import { RelocationDownloads } from '../components/shop/RelocationDownloads';
import { UpgradeOffer } from '../components/shop/UpgradeOffer';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const endsAt = (iso) => new Date(iso).toLocaleString('en-AU', { weekday: 'long', hour: 'numeric', minute: '2-digit' });

const ThankYouUpsell = ({ upsell }) => (
  <div className="mt-8 text-left" data-testid="thank-you-upsell">
    <UpgradeOffer quote={upsell} label="Today only" heading={`Get everything for A$${upsell.due}`}
      note={`Extra A$${upsell.discount} off your A$${upsell.regular_due} upgrade price, until ${endsAt(upsell.expires_at)}. Includes all three volumes, the interactive toolkit and the planner.`}
      start={(body) => axios.post(`${API_BASE}/upgrade-offer/checkout`, { token: upsell.token, payment_method: body.payment_method, origin_url: body.origin_url })} />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = ({ preset }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(!preset);
  const [success, setSuccess] = useState(!!preset);
  const [customerEmail, setCustomerEmail] = useState(preset?.customer_email || '');
  const [download, setDownload] = useState(preset?.download || null);
  const [upsell, setUpsell] = useState(preset?.upsell || null);

  useEffect(() => {
    if (preset) return;
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setLoading(false);
      setSuccess(true); // Assume success if no session_id (PayPal flow)
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/stripe/success?session_id=${sessionId}`);
      setSuccess(response.data.success);
      setCustomerEmail(response.data.customer_email || '');
      setDownload(response.data.download || null);
      setUpsell(response.data.upsell || null);
      if (response.data.success) trackPurchase(response.data.purchase);
    } catch (err) {
      console.error('Payment verification error:', err);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="mx-auto text-[#3B164C] animate-spin mb-4" />
          <p className="text-[#1E1622]/60">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="container-custom max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-[#E6DAC5] p-8 md:p-12 text-center"
        >
          {success ? (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <CheckCircle size={40} className="text-[#C5A059]" />
              </div>
              <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-4">
                Payment Successful!
              </h1>
              {download?.items?.length > 0 ? (
                <div className="mb-8" data-testid="checkout-downloads">
                  <p className="text-[#1E1622]/70 mb-6">Thank you for your purchase! Your downloads are ready below.
                    {customerEmail && <span className="block mt-2 font-medium">We've also emailed these to {customerEmail}.</span>}</p>
                  <RelocationDownloads download={download} />
                  {upsell && <ThankYouUpsell upsell={upsell} />}
                </div>
              ) : (<>
              <p className="text-[#1E1622]/70 mb-6">
                Thank you for your purchase! Your digital product will be delivered to your email shortly.
                {customerEmail && <span className="block mt-2 font-medium">Check your inbox at: {customerEmail}</span>}
              </p>
              <div className="bg-[#F5EFE9] rounded-2xl p-6 mb-8">
                <h3 className="font-semibold text-[#1E1622] mb-2">What happens next?</h3>
                <ul className="text-sm text-[#1E1622]/70 text-left space-y-2">
                  <li>• You'll receive an email with download links within a few minutes</li>
                  <li>• Check your spam folder if you don't see it</li>
                  <li>• Contact us if you have any issues: hello@theexpatstudio.com</li>
                </ul>
              </div>
              </>)}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/shop" className="btn-secondary flex items-center gap-2">
                  <ShoppingBag size={18} />
                  Continue Shopping
                </Link>
                <Link to="/orders" data-testid="checkout-my-orders-link" className="text-[#3B164C] font-medium hover:underline">My Orders</Link>
                <Link to="/" className="text-[#3B164C] font-medium hover:underline flex items-center gap-2">
                  Back to Home
                  <ArrowRight size={18} />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle size={40} className="text-red-500" />
              </div>
              <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-4">
                Payment Issue
              </h1>
              <p className="text-[#1E1622]/70 mb-8">
                There was an issue processing your payment. Please try again or contact our support team.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/shop" className="btn-primary flex items-center gap-2">
                  Try Again
                  <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="text-[#3B164C] font-medium hover:underline">
                  Contact Support
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

const PayPalCancelNote = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const token = searchParams.get('token');
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/payments/paypal/status?token=${encodeURIComponent(token)}`).then((r) => {
      if (['APPROVED', 'COMPLETED'].includes(r.data.status)) navigate(`/checkout/paypal-success?token=${encodeURIComponent(token)}`, { replace: true });
      else setInfo(r.data);
    }).catch(() => {});
  }, [token, navigate]);
  if (!info) return null;
  return (
    <div className="text-left bg-[#F5EFE9] rounded-2xl p-5 mb-8 text-sm text-[#1E1622]/80" data-testid="paypal-cancel-note">
      <p className="font-semibold text-[#1E1622] mb-1">PayPal didn't take a payment, so you haven't been charged.</p>
      {info.sandbox ? (
        <p>This was a practice PayPal checkout. Log in with your PayPal sandbox <strong>personal</strong> (buyer) account, not the sandbox business account that belongs to the shop, and then tap Pay.</p>
      ) : (
        <p>If you'd like to try again, go back to the guide and choose PayPal or card.</p>
      )}
    </div>
  );
};

const CheckoutCancel = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="container-custom max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-[#E6DAC5] p-8 md:p-12 text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
            <ShoppingBag size={40} className="text-[#C5A059]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-4">
            Checkout Cancelled
          </h1>
          <PayPalCancelNote />
          <p className="text-[#1E1622]/70 mb-8">
            No worries! Your cart is still waiting for you. Feel free to continue browsing or complete your purchase when you're ready.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/shop" className="btn-primary flex items-center gap-2">
              <ShoppingBag size={18} />
              Back to Shop
            </Link>
            <Link to="/" className="text-[#3B164C] font-medium hover:underline flex items-center gap-2">
              Back to Home
              <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const PayPalSuccess = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    const token = searchParams.get('token');
    
    if (paymentId && payerId) {
      executePayPalPayment(`paymentId=${encodeURIComponent(paymentId)}&PayerID=${encodeURIComponent(payerId)}`);
    } else if (token) {
      executePayPalPayment(`token=${encodeURIComponent(token)}`);
    } else {
      setLoading(false);
      setSuccess(false);
    }
  }, [searchParams]);

  const executePayPalPayment = async (query) => {
    try {
      const response = await axios.get(`${API}/payments/paypal/execute?${query}`);
      setSuccess(response.data.success);
      setResult(response.data);
      if (response.data.success) trackPurchase(response.data.purchase);
    } catch (err) {
      console.error('PayPal execute error:', err);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="mx-auto text-[#3B164C] animate-spin mb-4" />
          <p className="text-[#1E1622]/60">Processing your PayPal payment...</p>
        </div>
      </div>
    );
  }

  // Reuse the success/failure UI
  return success ? <CheckoutSuccess preset={result || {}} /> : (
    <div className="min-h-screen pt-24 pb-20">
      <div className="container-custom max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-[#E6DAC5] p-8 md:p-12 text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle size={40} className="text-red-500" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-4">
            PayPal Payment Failed
          </h1>
          <p className="text-[#1E1622]/70 mb-8">
            We couldn't complete your PayPal payment. Please try again or use a different payment method.
          </p>
          <Link to="/shop" className="btn-primary">
            Back to Shop
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export { CheckoutSuccess, CheckoutCancel, PayPalSuccess };
